import { useNavigate } from "@tanstack/react-location"
import { toast } from "materia"
import { useQueryClient } from "@tanstack/react-query"
import { type ReactNode, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useCurrentUserContext } from "../../../components/AppShell/CurrentUserProvider"
import {
  useChat,
  useChatState,
  useMessages,
  useTyping,
} from "../../hooks/useChat"
import { useChatDirectory } from "../../hooks/useChatDirectory"
import { useIsGroupManager } from "../../hooks/useGroup"
import { useNow } from "../../hooks/useNow"
import {
  seedPeerProfile,
  usePeerProfiles,
  type SelfProfile,
} from "../../hooks/usePeerProfiles"
import {
  useSharedItems,
  type SharedItem,
} from "../../../components/SharedProductCard"
import { useTypingNotifier } from "../../hooks/useTypingNotifier"
import { isHistoryLoaded } from "../../runtime/selectors"
import { getProductLink } from "../../../dataUtilities/productDataUtilities"
import {
  buildRoomJid,
  buildUserJid,
  isRoomJid,
  jidDomain,
  jidLocalPart,
} from "../../xmpp/jid"
import type { Jid } from "../../xmpp/jid"
import { Composer } from "../Composer"
import { DirectMenu } from "../DirectMenu"
import { ConnectionBanner } from "../ConnectionBanner"
import { NotificationPermissionBanner } from "../NotificationPermissionBanner"
import {
  GroupDialogsConnected,
  GroupMenu,
  type GroupDialog,
} from "../GroupActions"
import { NewChat, type Colleague } from "../NewChat"
import { SharePicker, type PickedProduct } from "../SharePicker"
import { Thread } from "../Thread"
import { conversationPath } from "./conversationPath"
import { eventHandles, toConversationRows } from "./conversationRows"
import { MessagesView } from "./MessagesView"
import { toThreadRows } from "./threadRows"

type MessagesViewConnectedProps = {
  /** From the route. The URL is what decides which conversation is open. */
  peer?: Jid
}

export function MessagesViewConnected({ peer }: MessagesViewConnectedProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const {
    connState,
    conversations,
    retractionWindowHours,
    openConversation,
    closeConversation,
    markActiveConversationRead,
    sendMessage,
    resendMessage,
    retractMessage,
    sendShare,
    attachmentUrl,
    uploadAttachment,
    ensureHandles,
    createGroup,
    startDirectConversation,
    reconnect,
    setGroupArchived,
    setConversationMuted,
  } = useChat()
  const colleagues = useChatDirectory()
  const [isPicking, setIsPicking] = useState(false)
  const [groupDialog, setGroupDialog] = useState<GroupDialog>()
  const [isCreating, setIsCreating] = useState(false)
  const [isSharing, setIsSharing] = useState(false)

  // The store follows the route, not the other way round: openConversation
  // marks it active, clears the unread flag and fills the thread in. With no
  // thread open (list view, or onBack), closeConversation clears the active
  // peer so it stops suppressing its unread badge (FAME-712). Leaving the view
  // entirely (a different route) must clear it too, or its last conversation
  // stays "open" and marks incoming messages read from elsewhere (FAME-858).
  useEffect(() => {
    if (peer) void openConversation(peer)
    else closeConversation()
    return () => closeConversation()
  }, [peer, openConversation, closeConversation])

  // A message that landed in the open thread while the window was unfocused is
  // unread; it is seen once the reader returns (FAME-858).
  useEffect(() => {
    window.addEventListener("focus", markActiveConversationRead)
    return () => window.removeEventListener("focus", markActiveConversationRead)
  }, [markActiveConversationRead])

  const directPeers = conversations
    .filter((conversation) => conversation.type === "direct")
    .map((conversation) => conversation.peer)

  const peersToResolve =
    peer && !isRoomJid(peer) ? [...directPeers, peer] : directPeers

  const selfJid = useChatState((state) => state.selfJid)
  const isHistoryIn = useChatState((state) => isHistoryLoaded(state, peer))
  const ownHandle = selfJid ? jidLocalPart(selfJid) : undefined

  // The directory never lists the caller, so our own handle can't resolve
  // through it — our own bubbles would show the "Someone" fallback. Resolve
  // our face from CurrentUserProvider instead (FAME-711).
  const { currentUser } = useCurrentUserContext()
  const self: SelfProfile | undefined = ownHandle
    ? {
        handle: ownHandle,
        profile: {
          displayName:
            currentUser.displayName || currentUser.name || currentUser.id,
          avatar: currentUser.avatar || null,
        },
      }
    : undefined

  const { resolvePeer, resolveHandle, resolveUserId } = usePeerProfiles(
    peersToResolve,
    // Members come with the row now (FAME-657), so every face on screen
    // resolves in the same batch as the peers — plus the handles a group
    // event's preview names (FAME-703).
    conversations.flatMap((conversation) => [
      ...(conversation.members ?? []),
      ...eventHandles(conversation.lastEvent),
    ]),
    self
  )

  const messages = useMessages(peer)
  // Retractability expires on the clock, so the thread needs a ticking one
  // while it is open — see `useNow`.
  const now = useNow(peer !== undefined)

  // Resolve every shared item in the open thread in one batched request; an id
  // the caller cannot open comes back absent and renders as no-access (FAME-453).
  const shareRefs = messages.flatMap((message) =>
    message.type === "chat" && message.share ? [message.share] : []
  )
  const { items: sharedItems, isError: sharesFailed } = useSharedItems(
    shareRefs.map((ref) => ref.id)
  )
  const resolvedShares =
    sharedItems && new Map(sharedItems.map((item) => [item.id, item]))

  const threadRows = toThreadRows(messages, {
    selfJid,
    resolveHandle,
    retractionWindowHours,
    t,
    now,
    resolvedShares,
    sharesFailed,
  })

  const rows = toConversationRows(conversations, {
    resolvePeer,
    resolveHandle,
    ownHandle,
    t,
  })

  /**
   * A brand-new direct conversation has no row yet: the server creates one on
   * the first message. So the header comes from the row when there is one and
   * from the resolved peer when there is not — otherwise picking someone lands
   * on their thread and finds the new-message prompt instead of a composer.
   */
  const openRow = rows.find((row) => row.peer === peer)
  const openName = peer
    ? (openRow?.name ?? resolvePeer(peer).displayName)
    : undefined
  const openFaces = peer ? (openRow?.faces ?? [resolvePeer(peer)]) : []
  const openConversationRow = conversations.find((one) => one.peer === peer)
  const openMembers = openConversationRow?.members
  const isArchived = openConversationRow?.archived ?? false
  const isMuted = openConversationRow?.muted ?? false
  const openGroupId = peer && openMembers ? jidLocalPart(peer) : undefined
  const isGroupManager = useIsGroupManager(peer, openGroupId)

  const toggleMute = () => {
    if (!peer) return
    void setConversationMuted(peer, !isMuted).catch(() =>
      toast(String(t("chat.MUTE_FAILED")))
    )
  }

  let threadMenu: ReactNode
  if (openName !== undefined && openGroupId) {
    threadMenu = (
      <GroupMenu
        name={openName}
        isManager={isGroupManager}
        isArchived={isArchived}
        isMuted={isMuted}
        onOpen={setGroupDialog}
        onUnarchive={() => void setGroupArchived(openGroupId, false)}
        onToggleMute={toggleMute}
      />
    )
  } else if (openName !== undefined && peer && !isRoomJid(peer)) {
    threadMenu = (
      <DirectMenu name={openName} isMuted={isMuted} onToggleMute={toggleMute} />
    )
  }
  const isOffline = connState !== "online"
  const composerNote = isArchived ? String(t("chat.ARCHIVED_NOTE")) : undefined

  const typing = useTyping(peer).map((handle) => ({
    handle,
    profile: resolveHandle(handle),
  }))
  // An archived group bounces whatever we send it.
  const typingNotifier = useTypingNotifier(peer, !isArchived)

  /** Our own JID's domain is the tenant every other JID is built against. */
  const tenant = selfJid ? jidDomain(selfJid) : undefined

  const startDirect = async (person: Colleague) => {
    if (!tenant) return

    // The picker knows people by user id; a DM is addressed by handle. This
    // mints one if they have never opened chat (FAME-449).
    const handles = await ensureHandles([person.id])
    const handle = handles[person.id]
    if (!handle) return

    // We already know who this is; hand resolution their profile so the empty
    // DM shows a name, not "Someone", before the resolve learns the handle we
    // just minted (FAME-713).
    seedPeerProfile(queryClient, handle, {
      displayName: person.displayName,
      avatar: person.avatar,
    })

    const peerJid = buildUserJid(handle, tenant)

    // The conversation exists from the moment it is opened (FAME-779), so an
    // abandoned composer still leaves it in the list.
    startDirectConversation(peerJid)

    setIsPicking(false)
    navigate({ to: conversationPath(peerJid) })
  }

  const openDm = (handle: string) => {
    if (!tenant) return
    navigate({ to: conversationPath(buildUserJid(handle, tenant)) })
  }

  // Learning items open as a modal under a view route, and it should overlay
  // the conversation it was shared in — so the card routes into the open
  // thread, not Explore (FAME-810). Only resolved (openable) items reach here —
  // a no-access card is not clickable.
  const openProduct = (item: SharedItem) => {
    if (!peer) return
    navigate({
      to: `${conversationPath(peer)}/${getProductLink(item.id, item.productType)}`,
    })
  }

  const uploadImage = (file: File, onProgress: (fraction: number) => void) =>
    peer
      ? uploadAttachment(peer, file, onProgress)
      : Promise.reject(new Error("no conversation open"))

  // Share the picked item as a reference, with an absolute deep link as the
  // body fallback (FAME-453) — so a client that can't resolve it still opens.
  const shareProduct = (product: PickedProduct) => {
    if (!peer) return

    const path = `/explore/${getProductLink(product.id, product.productType)}`
    const body = `${window.location.origin}${path}`
    void sendShare(peer, { type: "product", id: product.id }, body)
    setIsSharing(false)
  }

  const startGroup = async (name: string, members: Colleague[]) => {
    if (!tenant) return

    setIsCreating(true)
    try {
      const group = await createGroup(
        name,
        members.map((member) => member.id)
      )
      setIsPicking(false)
      navigate({ to: conversationPath(buildRoomJid(group.id, tenant)) })
    } finally {
      setIsCreating(false)
    }
  }

  return (
    <MessagesView
      conversations={rows}
      activePeer={peer}
      banner={
        <ConnectionBanner connState={connState} onReconnect={reconnect} />
      }
      listFooter={<NotificationPermissionBanner />}
      onOpen={(next) => navigate({ to: conversationPath(next) })}
      onNewMessage={() => setIsPicking(true)}
      thread={
        peer &&
        openName !== undefined && (
          <Thread
            key={peer}
            name={openName}
            faces={openFaces}
            memberCount={openMembers?.length}
            isDirect={!isRoomJid(peer)}
            isLoading={!isHistoryIn}
            messages={threadRows}
            typing={typing}
            onBack={() => navigate({ to: "/messages" })}
            menu={threadMenu}
            onAddMembers={
              openGroupId && isGroupManager && !isArchived
                ? () => setGroupDialog("add")
                : undefined
            }
            // Nothing to offer without a socket: retraction is a stanza, and
            // sending one while disconnected is a no-op the reader cannot see.
            onRetract={
              isOffline
                ? undefined
                : (messageId) => void retractMessage(peer, messageId)
            }
            // Only while there is a socket to send on — `resendMessage` needs a
            // ready session, so offering Retry offline would be a dead button.
            // The "Not sent" mark stays either way; the action returns with one.
            onResend={
              isOffline
                ? undefined
                : (messageId) => void resendMessage(peer, messageId)
            }
            // Groups only: in a DM the only author is the peer already open.
            onOpenDm={openGroupId ? openDm : undefined}
            onOpenProduct={openProduct}
            resolveAttachmentUrl={attachmentUrl}
            composer={
              <Composer
                conversationName={openName}
                // A dead socket drops a send (`sendMessage` needs a ready
                // session), and an archived group has its posts bounced.
                isOffline={isOffline}
                isDisabled={isArchived}
                note={composerNote}
                onDraftChange={typingNotifier.onDraftChange}
                onSend={(body, attachment) => {
                  typingNotifier.onSent()
                  void sendMessage(peer, body, attachment)
                }}
                onShareProduct={() => setIsSharing(true)}
                onUploadImage={uploadImage}
              />
            }
          />
        )
      }
      groupDialog={
        groupDialog &&
        openGroupId &&
        openName !== undefined && (
          <GroupDialogsConnected
            dialog={groupDialog}
            groupId={openGroupId}
            name={openName}
            memberHandles={openMembers ?? []}
            isManager={isGroupManager}
            userHandleToPeerProfile={resolveHandle}
            resolveUserId={resolveUserId}
            onClose={() => setGroupDialog(undefined)}
          />
        )
      }
      sharePicker={
        isSharing &&
        peer && (
          <SharePicker
            onShare={shareProduct}
            onClose={() => setIsSharing(false)}
          />
        )
      }
      newChat={
        isPicking && (
          <NewChat
            colleagues={colleagues ?? []}
            isCreating={isCreating}
            onStartDirect={(person) => void startDirect(person)}
            onCreateGroup={(name, members) => void startGroup(name, members)}
            onClose={() => setIsPicking(false)}
          />
        )
      }
    />
  )
}
